import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Circle, Square, X } from "lucide-react";

type Props = {
    isOpen: boolean;
    onCancel: () => void;
    onConfirm: (opts: { shape: "circle" | "square"; label: string }) => void;
    // Portal target so the modal is a descendant of the immersive/fullscreen
    // container and stacks above the board (issue #197). Defaults to body.
    portalContainer?: HTMLElement | null;
};

/** Choose an area (zone) shape and name before placing it on the board
 *  (issue #197). Self-portals so it renders above the immersive board and
 *  inside the OS-fullscreen element. */
export default function AddAreaModal({ isOpen, onCancel, onConfirm, portalContainer }: Props) {
    const { t } = useTranslation();
    const [shape, setShape] = useState<"circle" | "square">("circle");
    const [label, setLabel] = useState("");

    useEffect(() => {
        if (isOpen) {
            setShape("circle");
            setLabel("");
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const shapeBtn = (value: "circle" | "square", icon: React.ReactNode, text: string) => (
        <button
            type="button"
            onClick={() => setShape(value)}
            aria-pressed={shape === value}
            className={`flex min-h-[44px] flex-1 items-center justify-center gap-1.5 rounded-md border px-3 text-sm ${
                shape === value
                    ? "border-brand-500 bg-brand-50 ring-2 ring-brand-200"
                    : "border-gray-200 hover:border-brand-200"
            }`}
        >
            {icon} {text}
        </button>
    );

    return createPortal(
        <div
            className="fixed inset-0 z-[80] flex items-end justify-center bg-black/30 sm:items-center"
            onClick={onCancel}
        >
            <div
                className="w-full max-w-sm rounded-t-2xl bg-white p-6 shadow-xl sm:rounded-2xl"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-label={t("constellation.zone.add")}
            >
                <div className="mb-4 flex items-center justify-between">
                    <h2 className="text-base font-semibold text-gray-800">
                        {t("constellation.zone.add")}
                    </h2>
                    <button
                        type="button"
                        onClick={onCancel}
                        aria-label={t("common.close", "Close")}
                        className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100"
                    >
                        <X size={16} />
                    </button>
                </div>

                <p className="mb-1 text-xs font-semibold text-gray-500">
                    {t("constellation.table.shape")}
                </p>
                <div className="mb-4 flex gap-2">
                    {shapeBtn("circle", <Circle size={16} />, t("constellation.table.circle"))}
                    {shapeBtn("square", <Square size={16} />, t("constellation.table.square"))}
                </div>

                <label className="mb-1 block text-xs font-semibold text-gray-500">
                    {t("constellation.zone.name", "Name")}
                </label>
                <input
                    autoFocus
                    type="text"
                    value={label}
                    maxLength={40}
                    onChange={(e) => setLabel(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") onConfirm({ shape, label: label.trim() });
                    }}
                    placeholder={t("constellation.zone.namePlaceholder", "e.g. Past, Present")}
                    className="w-full rounded-md border px-3 py-2 text-sm outline-none focus:border-brand-300 focus:ring-2 focus:ring-brand-100"
                />

                <div className="mt-5 flex justify-end gap-2">
                    <button
                        type="button"
                        onClick={onCancel}
                        className="rounded-md border px-4 py-2 text-sm text-gray-600 hover:bg-gray-50"
                    >
                        {t("constellation.scenes.cancel")}
                    </button>
                    <button
                        type="button"
                        onClick={() => onConfirm({ shape, label: label.trim() })}
                        className="rounded-md bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700"
                    >
                        {t("constellation.zone.add")}
                    </button>
                </div>
            </div>
        </div>,
        portalContainer ?? document.body,
    );
}
